import type { TFunction } from 'i18next';
import type {
  TemplateChange,
  TemplateFactor,
  TemplateFactorInput,
  TemplateLanguage,
  TemplateQuestion,
  TemplateVersionContent,
  TemplateVersionSummary,
} from '../../../api/hms';
import { joinList } from '../hmsFormat';
import { untranslatedCounts } from './languagesView';
import { languageNameInText, MAIN_LANGUAGE } from '../hmsLanguages';

export const RECOMMENDED_QUESTIONS = 3;

/** Keys keep results comparable across versions */
export const toInput = (factors: TemplateFactor[]): TemplateFactorInput[] =>
  factors.map((factor) => ({
    key: factor.key,
    name: factor.name,
    description: factor.description,
    consequence: factor.consequence,
    dimension: factor.dimension,
    relevanceReason: factor.relevanceReason,
    questions: factor.questions.map((question) => ({
      key: question.key,
      text: question.text,
      highIsGood: question.highIsGood,
    })),
  }));

export type NewQuestion = Pick<TemplateQuestion, 'text' | 'highIsGood'>;

export const withQuestion = (
  factors: TemplateFactor[],
  factorIndex: number,
  question: NewQuestion,
  editIndex: number | null,
): TemplateFactorInput[] =>
  toInput(factors).map((factor, index) =>
    index !== factorIndex
      ? factor
      : {
          ...factor,
          questions:
            editIndex === null
              ? [...factor.questions, question]
              : factor.questions.map((existing, i) => (i === editIndex ? { ...existing, ...question } : existing)),
        },
  );

export const withoutQuestion = (factors: TemplateFactor[], factorIndex: number, questionIndex: number) =>
  toInput(factors).map((factor, index) =>
    index === factorIndex
      ? { ...factor, questions: factor.questions.filter((_, candidate) => candidate !== questionIndex) }
      : factor,
  );

export const withoutFactor = (factors: TemplateFactor[], factorIndex: number) =>
  toInput(factors).filter((_, index) => index !== factorIndex);

export type QuestionState = { isNew: boolean; edited: boolean };

/** Compared with the version in use */
export const questionState = (
  question: TemplateQuestion,
  factorKey: string,
  current: TemplateFactor[],
): QuestionState => {
  const before = current.find((factor) => factor.key === factorKey)?.questions.find((q) => q.key === question.key);
  return {
    isNew: !before,
    edited: Boolean(before) && (before?.text !== question.text || before?.highIsGood !== question.highIsGood),
  };
};

export const formatTemplateChange = (
  change: TemplateChange,
  t: TFunction,
  withCount = true,
  languages: TemplateLanguage[] = [],
) => {
  const nameOf = (code: string) =>
    languageNameInText(languages.find((language) => language.code === code) ?? { code, name: null }, t);
  switch (change.type) {
    case 'languagesChanged':
      return t('hrRiskFactors.changes.languagesChanged', {
        languages: [MAIN_LANGUAGE, ...change.languages].map(nameOf).join(', '),
      });
    case 'translationsChanged':
      return t('hrRiskFactors.changes.translationsChanged', { language: nameOf(change.language) });
    case 'dimensionChanged':
      return t(`hrRiskFactors.changes.dimensionChanged.${change.to}`, { name: change.name });
    case 'factorRemoved':
      return t('hrRiskFactors.changes.factorRemoved', { name: change.name });
    case 'factorAdded':
      return withCount
        ? t('hrRiskFactors.changes.factorAddedWithQuestions', { name: change.name, count: change.questions })
        : t('hrRiskFactors.changes.factorAdded', { name: change.name });
    default:
      return t(`hrRiskFactors.changes.${change.type}`, { name: change.name, count: change.count });
  }
};

/** The warning shown before a draft is sent: missing translations, removed statutory factors and factors with few questions */
export const sendWarning = (
  draft: TemplateVersionContent,
  current: TemplateVersionContent,
  t: TFunction,
) => {
  const nameOf = (factors: TemplateFactor[], key: string) =>
    factors.find((factor) => factor.key === key)?.name ?? key;
  const untranslated = untranslatedCounts(draft.languages);
  const removed = draft.warnings.legallyRequiredRemoved.map((key) => nameOf(current.factors, key).toLowerCase());
  const few = draft.warnings.fewQuestions.map((key) => nameOf(draft.factors, key));
  return [
    untranslated.length > 0 &&
      t('hrRiskFactors.send.untranslated', {
        languages: joinList(
          untranslated.map(({ language, missing }) =>
            t('hrRiskFactors.send.untranslatedLanguage', { count: missing, language: languageNameInText(language, t) }),
          ),
          t,
          'and',
        ),
      }),
    removed.length > 0 && t('hrRiskFactors.send.legallyRequired', { names: joinList(removed, t, 'and') }),
    few.length > 0 && t('hrRiskFactors.send.fewQuestions', { names: joinList(few, t, 'and') }),
  ]
    .filter(Boolean)
    .join(' ');
};

type TagColor = 'success' | 'info' | 'neutral' | 'warning';

/** The status tag of a template version */
export const versionStatus = (version: TemplateVersionSummary): { label: string; color: TagColor } => {
  if (version.status === 'PENDING') return { label: 'hrRiskFactors.status.pending', color: 'warning' };
  if (version.recommended) return { label: 'hrRiskFactors.status.recommended', color: 'success' };
  if (version.availableForNew) return { label: 'hrRiskFactors.status.available', color: 'info' };
  return { label: 'hrRiskFactors.status.unavailable', color: 'neutral' };
};

